import { getErrorJson } from '@mm/utils';
import { Dispatch } from 'redux';
import { NOT_FOUND } from '@mm/constants/status-codes';
import { InstagramController } from '../controllers/InstagramController';
import commonHandler from '../errorHandlers/common-handler';
import {
  instagramHandler,
  ResponseOrError,
} from '../errorHandlers/instagram-handler';
import {
  MessageStructure,
  IgMessageStructure,
} from '../model/LivechatMessageModel';
import modalActions from '../modules/modalsState/actions';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import connectedPageCRUDActions from './ConnectedPageCRUDActions';
import CrudActionContainer from './CrudActionContainer';
import { InstagramMediaModel } from '../interfaces/instagram';
import linkSrc from 'assets/link.png';

type StoryResponse = {
  id: number;
  story: MessageStructure['story'];
  message?: string;
};

type MessageResponse = {
  structures?: IgMessageStructure[];
};

class InstagramCRUDActions extends CrudActionContainer {
  retrieveAgentGroupsAction: string;
  resetAction: string;
  getIGAccountsAction: string;
  getIGListMediaAction: string;
  getIGMediaAction: string;

  constructor() {
    super('INSTAGRAM');
    this.getIGAccountsAction = this.makeActionKey('GET_IG_ACCOUNTS_ACTION');
    this.getIGListMediaAction = this.makeActionKey('GET_IG_LIST_MEDIA_ACTION');
    this.getIGMediaAction = this.makeActionKey('GET_IG_MEDIA_ACTION');
  }

  getIGAccounts(fbPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await InstagramController.getIGAccounts(fbPageId);
        dispatch({
          type: this.getIGAccountsAction,
          instagramAccounts: response.instagramAccounts,
        });
        return response;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  activateIG(
    fbPageId: number,
    shallow: boolean = false,
    skipPageRetrieve = false,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      const handleError = async (e: ResponseOrError) => {
        if (e instanceof Response) {
          const responseErrorBody = await getErrorJson(e);
          const errorMessage = responseErrorBody.errors?.length
            ? responseErrorBody.errors[0]
            : '';
          dispatch(
            modalActions.openPrettyErrorModal({
              title: 'Failed to connect Instagram Account',
              contactSupport: true,
              errorMessage:
                'Something went wrong when we tried to connect to your Instagram account.',
              detailsForNerds: `(${e.status}): ${errorMessage} ${
                responseErrorBody.error_code
                  ? `[error_code: ${responseErrorBody.error_code}]`
                  : ''
              }`,
              image: linkSrc,
            }),
          );
        }
        return instagramHandler(dispatch)(e);
      };

      try {
        const response = await InstagramController.activateIG(
          fbPageId,
          shallow,
        );
        dispatch(this.getIGAccounts(fbPageId));
        if (!skipPageRetrieve) {
          dispatch(connectedPageCRUDActions.retrievePage(fbPageId));
        }
        return response;
      } catch (e) {
        if (window.MMApp?.throwErrors) {
          throw e;
        }
        dispatch(this.getIGAccounts(fbPageId));
        if (!skipPageRetrieve) {
          dispatch(connectedPageCRUDActions.retrievePage(fbPageId));
        }
        handleError(e);
      }
    };
  }

  deactivateIG(fbPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await InstagramController.deactivateIG(fbPageId);
        dispatch(this.getIGAccounts(fbPageId));
        dispatch(connectedPageCRUDActions.retrievePage(fbPageId));
        return response;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  getIGStory(mid: string, fbPageId: number, extraOptions?: {}) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<StoryResponse | undefined> => {
      try {
        const response = await InstagramController.getIGStory(mid, fbPageId);
        return response;
      } catch (e) {
        if (e instanceof Response) {
          if (e.status === NOT_FOUND) {
            return;
          }
        }
        return commonHandler(dispatch)(e);
      }
    };
  }

  getRemoteIGMessage(mid: string, fbPageId: number) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<StoryResponse | undefined> => {
      try {
        const response = await InstagramController.getRemoteIGMessage(
          mid,
          fbPageId,
        );
        return response;
      } catch (e) {
        if (e instanceof Response) {
          if (e.status === NOT_FOUND) {
            return;
          }
        }
        return commonHandler(dispatch)(e);
      }
    };
  }

  getLocalIGMessage(
    mid: string,
    fbPageId: number,

    remoteContactId: string,
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<MessageResponse | undefined> => {
      try {
        const response = await InstagramController.getLocalIGMessage(
          mid,
          fbPageId,
          remoteContactId,
        );
        // if (response.structures?.length) {
        //   return {
        //     ...response,
        //     structures: response.structures.map(s => JSON.parse(s)),
        //   };
        // }
        return response;
      } catch (e) {
        if (e instanceof Response) {
          if (e.status === NOT_FOUND) {
            return;
          }
        }
        return commonHandler(dispatch)(e);
      }
    };
  }

  getIGListMedia(
    fbPageId: number,
    cursor: string = '',
    cursorValue: string = '',
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<{ data: InstagramMediaModel[] } | undefined> => {
      try {
        const response = await InstagramController.getIGListMedia(
          fbPageId,
          cursor,
          cursorValue,
        );
        dispatch({
          type: this.getIGListMediaAction,
          instagramListMedia: response.data,
          instagramListMediaPaging: response.paging,
        });
        return response;
      } catch (e) {
        if (e instanceof Response) {
          if (e.status === NOT_FOUND) {
            return;
          }
        }
        return commonHandler(dispatch)(e);
      }
    };
  }

  getIGMedia(fbPageId: number, mediaId: string) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<{ data: InstagramMediaModel }> => {
      try {
        const response = await InstagramController.getIGMedia(
          fbPageId,
          mediaId,
        );
        dispatch({
          type: this.getIGMediaAction,
          instagramMedia: response,
        });
        return response;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  clearIGMedia() {
    return async (dispatch: Dispatch<StoreState>) => {
      await dispatch({
        type: this.getIGMediaAction,
        instagramMedia: [],
      });
    };
  }
}

const instagramCRUDActions = new InstagramCRUDActions();

export const useInstagramCRUDActions = getActionHookBuilder(
  instagramCRUDActions,
  [
    'activateIG',
    'getIGAccounts',
    'deactivateIG',
    'getIGStory',
    'getRemoteIGMessage',
    'getLocalIGMessage',
    'getIGListMedia',
    'getIGMedia',
    'clearIGMedia',
  ],
);

export default instagramCRUDActions;
